import React from "react";
import styled from "styled-components";
import ThemedModal from "../../utilities/ThemedModal";
import { Modal } from "react-bootstrap";
import { Button } from "../..";
import { FormProvider, useForm } from "react-hook-form";
import { ClientReportType } from "../../../types/ClientReportType";
import { DefEntityPatchOperations, EntityPatchDocument } from "../../../types/DefEntityPatchOperations";
import { BaseModalOptions, ModalFormConfirmation } from "../../../types/DefModalOptions";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { FormBody } from "./components/FormBody";
import { MessageForm } from "./components/MessageForm";
import { ReportReadonlyMessage } from "../../messages";
import { ReportControlProvider, useReportControls } from "./hooks/ReportControlContext";
import { ReportMessageProvider, useReportMessage } from "./hooks/ReportMessageContext";
import { ReportPublishProvider, useReportSendOptions } from "./hooks/ReportPublishContext";
import { openClientReportHistoryModal } from "../client-report-history";
import { usePatchReport } from "./hooks/usePatchReport";
import { PatchReportPublishEmailOptions, PatchReportRequest } from "../../../services/clientreports/types";
import { useMailMessageForm } from "../../../hooks/useMailMessageForm";

export type EditClientReportOptions = ModalFormConfirmation<ClientReportType, ClientReportType>;
export type BaseClientReportOptions = BaseModalOptions & {
    defaultValues: ClientReportType
};

const ModalBody = styled(Modal.Body)`
    min-height: auto;
    max-height: 66.67vh;
    display: flex;
    flex-direction: column;
    padding: 0;
`;

const EditClientReportModal = ({ 
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    isEdit,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<EditClientReportOptions>) => {
    const formMethods = useForm<ClientReportType>();
    const { formState, getValues, handleSubmit, reset } = formMethods;
    const { report, isReportEditable } = useReportControls();
    const { message } = useReportMessage();
    const { sendOptions } = useReportSendOptions();
    const { convertToForm } = useMailMessageForm('new');
    const { isLoading, patchReport } = usePatchReport(defaultValues);

    const onClose = () => cancel!();

    const onOpenHistory = (e: any) : void => {
        if (e && typeof(e?.preventDefault) === 'function') {
            e.preventDefault();
        }
        if (report) {
            openClientReportHistoryModal(report.masterAccountId, report.id).then();
        }
    };

    const onSubmit = (form: ClientReportType) : void => {
        const { dirtyFields } = formState;
        if (!dirtyFields || Object.keys(dirtyFields).length === 0) {
            onClose();
        }

        const keys = Object.keys(dirtyFields) as Array<keyof ClientReportType>;
        const operations = keys.map(el => {
            return { op: 'replace', path: `/${el}`, value: getValues(el) } as DefEntityPatchOperations;
        });

        if (!operations || operations.length === 0) {
            onClose();
        }

        // set up the email message options that we will send over to graph to setup the draft mail
        let emailForm : FormData | null = null;
        let emailOptions : PatchReportPublishEmailOptions | null = null;
        if (sendOptions?.mailMessage) {
            const mailMessage = sendOptions.mailMessage;
            const reportAttachment = mailMessage.attachments?.find(el => el.documentId === form.id);
            const businessSheetAttachment = mailMessage.attachments?.find(el => el.documentId === form.businessSheet?.businessSheetId);

            emailForm = convertToForm(sendOptions.mailMessage);
            emailOptions = {
                mailMessageId: null,
                mailSenderId: mailMessage.from,
                isReportIncluded: reportAttachment ? true : false,
                isBusinessSheetIncluded: businessSheetAttachment ? true : false,
                reportDocumentPassword: reportAttachment?.password,
                businessSheetDocumentPassword: businessSheetAttachment?.password
            };
        }

        // create the request that goes to the report service to update the status and properties of this report
        // if the send options/publish options are set then it will trigger all the e-signatures, etc that we want
        // it will also set the business sheet up and go to head office.
        const patchReportRequest : PatchReportRequest = {
            masterAccountId: form.masterAccountId,
            reportId: form.id,
            operations,
            messageDetails: message,
            publishDetails: sendOptions ? {
                mediaType: sendOptions.mediaType,
                adviserMessage: sendOptions.adviserMessage,
                emailAddressesToNotify: sendOptions.emailAddressesToNotify,
                isNotifiedViaDashboard: sendOptions.isNotifiedViaDashboard,
                isNotifiedViaEmail: sendOptions.isNotifiedViaEmail,
                emailOptions: emailOptions
            } : null
        };

        patchReport(patchReportRequest, emailForm).then((report: ClientReportType) => proceed!(report));
    };

    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        formMethods.reset({ ...defaultValues });
    }, [ defaultValues, reset ]);

    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Edit Report</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        {!isReportEditable && (
                            <ReportReadonlyMessage 
                                text={(<span>This Report is now Readonly. You can view the history <a href="#" className="link-secondary" onClick={onOpenHistory}>here</a>.</span>)}
                            />
                        )}
                        <section className="p-3">
                            <FormBody isLoading={isLoading} />
                            <MessageForm isLoading={isLoading} />
                        </section>
                    </ModalBody>
                    <Modal.Footer>
                        <Button variant="success" type="submit" disabled={isLoading}>{message == null ? 'Save Changes' : 'Save Changes & Send Message'}</Button>
                        <Button variant="light" onClick={onClose} disabled={isLoading}>Close</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );
};

export function openEditClientReportModal(options: BaseClientReportOptions) : Promise<ClientReportType> {
    return createConfirmation<EditClientReportOptions, ClientReportType>(confirmable((props) => (
        <ReportControlProvider data={options.defaultValues}>
            <ReportMessageProvider data={options.defaultValues}>
                <ReportPublishProvider data={options.defaultValues}>
                    <EditClientReportModal {...props} />
                </ReportPublishProvider>
            </ReportMessageProvider>
        </ReportControlProvider>
    )))({
        dialogId: 'client_report_edit_modal',
        isEdit: true,
        defaultValues: options.defaultValues
    });
}